import React, { useState } from "react";
import { useLoaderData } from "react-router-dom";
import { Stack } from "@mui/material";
import TabLayout from "../../helpers/TabLayout";
import { sortEmailsByCreateDate } from "../../../api/helpers";
import useInfoSnackbar from "../../../hooks/useInfoSnackbar";
import EmailItem from "./EmailItem";
import EmailForm from "./EmailForm";
import EmailList from "./EmailList";
import {
  sessionCreateUserEmail,
  sessionDeleteUserEmail,
  sessionListUserEmail,
  sessionView,
} from "../../../api/session";
import { testSessionLoader } from "../../../loaders/helpers";

const emailsTabLoader = async (params) => {
  await testSessionLoader(params);
  const userEntityRes = await sessionView();
  const listEmailsRes = await sessionListUserEmail();
  const allEmails = listEmailsRes.data;
  const primaryEmail = allEmails.find(
    email => email.userEmailEntityId === userEntityRes.data.userEmailEntityId,
  );
  const secondaryEmails = allEmails.filter(
    email => email.userEmailEntityId !== primaryEmail?.userEmailEntityId,
  );
  const sortedEmails = sortEmailsByCreateDate(secondaryEmails);

  return { primaryEmail, secondaryEmails: sortedEmails };
};

const EmailsTab = () => {
  const loaderData = useLoaderData();
  const { primaryEmail, secondaryEmails } = loaderData;
  const [newEmail, setNewEmail] = useState("");
  const infoSnackbar = useInfoSnackbar(3000);

  const refreshEmails = async () => {
    const reloadedData = await emailsTabLoader();
    loaderData.primaryEmail = reloadedData.primaryEmail;
    loaderData.secondaryEmails = reloadedData.secondaryEmails;
  };

  const handleEmailDelete = async (email, emailId) => {
    const response = await sessionDeleteUserEmail(email, emailId);
    if (response.code === "SUCCESS") {
      await refreshEmails();
      infoSnackbar.alertSuccess(response.message);
    } else {
      infoSnackbar.alertError("Something went wrong...");
    }
  };

  const handleNewEmailSubmit = async () => {
    const response = await sessionCreateUserEmail(newEmail.trim());
    if (response.code === "SUCCESS") {
      await refreshEmails();
      infoSnackbar.alertSuccess(response.message);
      setNewEmail("");
    } else {
      infoSnackbar.alertError(response.message);
    }
  };

  const handleNewEmailChange = (event) => setNewEmail(event.target.value);

  return (
    <TabLayout
      headerLabel="Emails"
      skeletonLines={5 + (2 * secondaryEmails.length ?? 0)}
    >
      <Stack spacing={2}>
        <EmailList label="Primary Email">
          {primaryEmail && (
            <EmailItem
              key={primaryEmail.userEmailEntityId}
              emailObj={primaryEmail}
            />
          )}
        </EmailList>
        <EmailList label="Secondary Emails">
          {secondaryEmails.map((emailObj) => (
            <EmailItem
              key={emailObj.userEmailEntityId}
              emailObj={emailObj}
              onEmailDeleted={() => handleEmailDelete(
                emailObj.email,
                emailObj.userEmailEntityId)}
            />
          ))}
          {secondaryEmails.length < 5 &&
            <EmailForm
              newEmail={newEmail}
              onNewEmailChanged={handleNewEmailChange}
              onNewEmailSubmit={handleNewEmailSubmit}
            />}
        </EmailList>
      </Stack>
    </TabLayout>
  );
};

export { emailsTabLoader };
export default EmailsTab;